import { createRandomID } from '../../reusable/reusableFunctions.js';

export const STREAM_INACTIVITY_TIMEOUT_MS = Number(process.env.STREAM_INACTIVITY_TIMEOUT_MS) || 20000;
export const DEFAULT_CHATBOT_ERROR_MESSAGE = 'Failed to connect to the server. Please check your network connection and try again.';
export const DEFAULT_ANSWER_FOR_FIND_SAVINGS = 'No response details available.';
const ESCAPED_NEWLINE_REGEX = /\\n/g;
const WRAPPED_DOUBLE_QUOTES_REGEX = /^"|"$/g;

export function buildPayload(messages, threadId, currentPrompt) {
  const lastMessage = Array.isArray(messages) ? messages[messages.length - 1] : messages;
  const query = typeof lastMessage === 'string' ? lastMessage : lastMessage?.content ?? lastMessage?.text ?? '';
  const type = lastMessage?.meta?.type ?? currentPrompt?.type ?? 'chat';
  const rawContext = lastMessage?.meta?.context ?? currentPrompt?.context ?? {};
  const context = typeof rawContext === 'string' ? rawContext : JSON.stringify(rawContext);

  return { query, type, conversation_id: threadId, context };
}

export function formatThreadMessages(messages) {
  return messages.map((msg) => ({
    ...msg,
    ...(msg.id != null ? { id: String(msg.id) } : {}),
    content: msg.role === 'user' ? normalizePythonQuestion(msg.content) : normalizePythonMessage(msg.content)
  }));
}

export function injectUserQuestion(questionText) {
  if (!questionText) return;

  const chatbot = document.querySelector('forge-ai-chatbot');

  if (chatbot && typeof chatbot.getMessages === 'function') {
    const currentMessages = chatbot.getMessages() || [];

    const userMessage = {
      id: `q-${createRandomID()}`,
      role: 'user',
      content: questionText,
      timestamp: Date.now(),
    };

    chatbot.setMessages([...currentMessages, userMessage]);

    if (typeof chatbot.scrollToBottom === 'function') {
      chatbot.scrollToBottom();
    }
  }
}

export function updateTextTurnQueue(turnQueue, normalizedOutput) {
  if (!normalizedOutput) return;

  let lastTurn = turnQueue[turnQueue.length - 1];

  if (!lastTurn || lastTurn.type !== 'text') {
    lastTurn = {
      type: 'text',
      targetText: '',
      typedText: '',
      targetCumulativeLength: 0,
      normalizedCumulativeLength: 0,
    };
    turnQueue.push(lastTurn);
  }

  if (normalizedOutput.length < lastTurn.targetText.length || lastTurn.targetCumulativeLength < lastTurn.targetText.length) {
    if (normalizedOutput.length < lastTurn.normalizedCumulativeLength) {
      lastTurn.targetCumulativeLength = lastTurn.targetText.length;
    }

    lastTurn.targetText = lastTurn.targetText.slice(0, lastTurn.targetCumulativeLength) + normalizedOutput;
    lastTurn.normalizedCumulativeLength = normalizedOutput.length;
  } else if (lastTurn.targetCumulativeLength === lastTurn.targetText.length) {
    lastTurn.targetText = normalizedOutput;
    lastTurn.targetCumulativeLength = lastTurn.targetText.length;
  }
}

export function getNextTypingStep(currentTurn) {
  const remaining = currentTurn.targetText.length - currentTurn.typedText.length;

  if (remaining <= 0) {
    return { delta: '', isFinished: true };
  }

  const charactersToPrint = remaining > 300 ? 4 : 2;
  const delta = currentTurn.targetText.substr(currentTurn.typedText.length, charactersToPrint);
  const updatedTypedText = currentTurn.typedText + delta;
  const isFinished = updatedTypedText.length >= currentTurn.targetText.length;

  return { delta, updatedTypedText, isFinished };
}

export function createSseLineProcessor({ onResetTimeout, onQuestion, onMessage }) {
  let currentEvent = 'message';

  return (line) => {
    const trimmed = line.trim();
    if (!trimmed) return;

    if (trimmed.startsWith('event:')) {
      currentEvent = trimmed.replace('event:', '').trim();
      return;
    }

    if (!trimmed.startsWith('data:')) return;

    const data = trimmed.replace(/^data:\s*/, '');
    onResetTimeout();

    if (!data) {
      return;
    }

    if (currentEvent === 'question') {
      const questionText = normalizePythonQuestion(data);
      onQuestion(questionText);
      return;
    }

    if (currentEvent === 'message') {
      const text = normalizePythonMessage(data);
      onMessage(text);
    }
  };
}

export async function processSseStream(response, processLine) {
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split(/\r?\n/);
    buffer = lines.pop() || '';
    lines.forEach(processLine);
  }

  buffer += decoder.decode();
  if (buffer.trim()) processLine(buffer);
}

export const normalizePythonQuestion = (rawText) => {
  if (!rawText) return '';

  let text = rawText;
  text = text.replace(ESCAPED_NEWLINE_REGEX, '\n');
  text = text.replace(WRAPPED_DOUBLE_QUOTES_REGEX, '');
  return text;
};

export const normalizePythonMessage = (rawText) => {
  if (!rawText || rawText === DEFAULT_ANSWER_FOR_FIND_SAVINGS) return '';
  let text = rawText;

  try {
    let jsonValidText = text.trim();
    if (!jsonValidText.startsWith('"')) jsonValidText = `"${jsonValidText}"`;
    text = JSON.parse(jsonValidText);
  } catch (e) {
    text = text.replace(WRAPPED_DOUBLE_QUOTES_REGEX, '');
    text = text.replace(ESCAPED_NEWLINE_REGEX, '\n');
  }

  text = text.replace(/\*\*\s*([^*]+?)\s*\*\*/g, '**$1**');
  text = text.replace(/(\d+\.)\*\*/g, '$1 **');
  text = text.replace(/^\s*-\s+/gm, '* ');
  text = text.replace(/(\n|^)(\d+\.\s+\*\*)/g, '\n\n$2');
  text = text.replace(/[═]{2,}([^*]+?)[═]{2,}/gm, '#### $1');

  const headerPattern = /(SECTION:|CONCLUSION AND NEXT STEPS|RECOMMENDED ACTIONS|EXECUTIVE SUMMARY|PHASE \d+:?|PROGRAM \d+:?|HYPOTHESIS \d+:?|OPPORTUNITY \d+:?|CRITICAL\s+[A-Z\s]+:|FOR\s+"[^"]+":?|(?:\n|^)\s*\d+\.\s+[A-Z\s]{4,}:?)/g;
  text = text.replace(headerPattern, (match) => { return `\n\n### ${match.trim()}\n`; });
  text = text.replace(/(###\s*){2,}/g, '### ');
  text = text.replace(/\n{3,}/g, '\n\n');

  return text.trim();
};

export const updateToolbarInteractivity = (toolbar, isOpen) => {
  const toolbarContainer = toolbar.getRootNode?.()?.querySelector?.('.toolbar-container');
  if (toolbarContainer) {
    if (isOpen) {
      toolbarContainer.style.pointerEvents = 'auto';
      toolbarContainer.style.opacity = '1';
      toolbarContainer.style.transform = 'translateY(0)';
    } else {
      toolbarContainer.style.removeProperty('pointer-events');
      toolbarContainer.style.removeProperty('opacity');
      toolbarContainer.style.removeProperty('transform');
    }
  }
  const popoverContent = toolbar.shadowRoot?.querySelector('.popover-content');
  if (popoverContent) {
    if (isOpen) {
      popoverContent.style.pointerEvents = 'auto';
    } else {
      popoverContent.style.removeProperty('pointer-events');
    }
  }
};
